import './mermaid-editor-menu';

import { CaptionedBlockComponent } from '@blocksuite/affine-components/caption';
import { createLitPortal } from '@blocksuite/affine-components/portal';
import { css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import mermaid from 'mermaid';

import type { PiqueMermaidBlockModel } from './mermaid-block';

mermaid.initialize({
  startOnLoad: false,
  securityLevel: 'strict',
  theme: 'default',
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: 'basis',
  },
});

export const mermaidBlockStyles = css`
  affine-mermaid {
    display: block;
  }

  .mermaid-block-container {
    display: flex;
    position: relative;
    width: 100%;
    min-height: 100px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    overflow: hidden;
    user-select: none;
    background: var(--affine-background-secondary-color);
  }

  .mermaid-block-container:hover {
    background: var(--affine-hover-color);
  }

  .mermaid-viewport {
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: relative;
  }

  .mermaid-viewport.zoomed {
    cursor: grab;
  }

  .mermaid-viewport.panning {
    cursor: grabbing;
  }

  .mermaid-diagram-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100px;
    padding: 16px;
    transform-origin: center center;
    transition: transform 0.1s ease-out;
    will-change: transform;
  }

  .mermaid-diagram-wrapper.no-transition {
    transition: none;
  }

  .mermaid-diagram-wrapper svg {
    max-width: none;
    height: auto;
  }

  .mermaid-controls {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    gap: 4px;
    opacity: 0;
    transition: opacity 0.2s;
    z-index: 10;
  }

  .mermaid-block-container:hover .mermaid-controls {
    opacity: 1;
  }

  .mermaid-control-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 4px;
    background: var(--affine-background-overlay-panel-color);
    border: 1px solid var(--affine-border-color);
    cursor: pointer;
    color: var(--affine-icon-color);
  }

  .mermaid-control-btn:hover {
    background: var(--affine-hover-color);
  }

  .mermaid-control-btn svg {
    width: 16px;
    height: 16px;
  }

  .mermaid-zoom-label {
    display: flex;
    align-items: center;
    padding: 0 8px;
    font-size: 12px;
    color: var(--affine-text-secondary-color);
    background: var(--affine-background-overlay-panel-color);
    border: 1px solid var(--affine-border-color);
    border-radius: 4px;
    cursor: pointer;
  }

  .mermaid-zoom-label:hover {
    background: var(--affine-hover-color);
  }

  .mermaid-error-placeholder {
    color: var(--affine-error-color, #eb4335);
    font-family: Inter, sans-serif;
    font-size: 12px;
    font-weight: 500;
    padding: 24px;
    text-align: center;
  }

  .mermaid-empty-placeholder {
    color: var(--affine-placeholder-color);
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 500;
    padding: 24px;
    text-align: center;
  }

  .mermaid-pan-hint {
    position: absolute;
    bottom: 8px;
    left: 50%;
    transform: translateX(-50%);
    padding: 4px 8px;
    background: var(--affine-background-overlay-panel-color);
    border: 1px solid var(--affine-border-color);
    border-radius: 4px;
    font-size: 11px;
    color: var(--affine-text-secondary-color);
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
  }

  .mermaid-block-container:hover .mermaid-pan-hint.visible {
    opacity: 1;
  }
`;

@customElement('affine-mermaid')
export class MermaidBlockComponent extends CaptionedBlockComponent<PiqueMermaidBlockModel> {
  static override styles = mermaidBlockStyles;

  private _editorAbortController: AbortController | null = null;
  private _renderCounter = 0;
  private _isPanning = false;
  private _panStart = { x: 0, y: 0 };
  private _lastPan = { x: 0, y: 0 };

  @query('.mermaid-viewport')
  private accessor _viewport!: HTMLDivElement;

  @query('.mermaid-diagram-wrapper')
  private accessor _diagramWrapper!: HTMLDivElement;

  @state()
  private accessor _zoom = 1;

  @state()
  private accessor _panX = 0;

  @state()
  private accessor _panY = 0;

  @state()
  private accessor _error: string | null = null;

  @state()
  private accessor _svgContent: string | null = null;

  get isBlockSelected() {
    const blockSelection = this.selection.filter('block');
    return blockSelection.some(sel => sel.blockId === this.model.id);
  }

  get isZoomed() {
    return this._zoom !== 1 || this._panX !== 0 || this._panY !== 0;
  }

  override connectedCallback() {
    super.connectedCallback();
    this.contentEditable = 'false';
  }

  override firstUpdated(props: Map<string, unknown>) {
    super.firstUpdated(props);

    const { disposables } = this;

    this._editorAbortController?.abort();
    this._editorAbortController = new AbortController();
    disposables.add(() => {
      this._editorAbortController?.abort();
    });

    this._renderMermaid();

    disposables.add(
      this.model.propsUpdated.on(() => {
        this._renderMermaid();
      })
    );
  }

  private async _renderMermaid() {
    const code = this.model.code;
    this._renderCounter++;
    const currentRender = this._renderCounter;

    if (!code || code.trim().length === 0) {
      this._svgContent = null;
      this._error = null;
      return;
    }

    try {
      const id = `mermaid-${this.model.id}-${currentRender}`;
      const { svg } = await mermaid.render(id, code);

      if (currentRender !== this._renderCounter) return;

      this._svgContent = svg;
      this._error = null;
    } catch (err) {
      if (currentRender !== this._renderCounter) return;

      this._svgContent = null;
      this._error = err instanceof Error ? err.message : 'Invalid Mermaid syntax';
    }
  }

  private _handleClick(_e: MouseEvent) {
    if (this._isPanning) return;
    if (this.doc.readonly) return;

    if (this.isBlockSelected) {
      this.toggleEditor();
    } else {
      this.selectBlock();
    }
  }

  private _handleWheel(e: WheelEvent) {
    if (!this._svgContent) return;

    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      e.stopPropagation();

      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const newZoom = Math.max(0.25, Math.min(5, this._zoom + delta));

      if (newZoom !== this._zoom) {
        const rect = this._viewport?.getBoundingClientRect();
        if (rect) {
          const mouseX = e.clientX - rect.left - rect.width / 2;
          const mouseY = e.clientY - rect.top - rect.height / 2;

          const zoomRatio = newZoom / this._zoom;
          this._panX = mouseX - (mouseX - this._panX) * zoomRatio;
          this._panY = mouseY - (mouseY - this._panY) * zoomRatio;
        }

        this._zoom = newZoom;
      }
    } else if (this._zoom > 1) {
      e.preventDefault();
      e.stopPropagation();

      this._panX -= e.deltaX;
      this._panY -= e.deltaY;
      this._constrainPan();
    }
  }

  private _handlePointerDown(e: PointerEvent) {
    if (!this._svgContent) return;
    if (this._zoom <= 1 && this._panX === 0 && this._panY === 0) return;

    e.preventDefault();
    this._isPanning = true;
    this._panStart = { x: e.clientX, y: e.clientY };
    this._lastPan = { x: this._panX, y: this._panY };

    if (this._diagramWrapper) {
      this._diagramWrapper.classList.add('no-transition');
    }

    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }

  private _handlePointerMove(e: PointerEvent) {
    if (!this._isPanning) return;

    const deltaX = e.clientX - this._panStart.x;
    const deltaY = e.clientY - this._panStart.y;

    this._panX = this._lastPan.x + deltaX;
    this._panY = this._lastPan.y + deltaY;
    this._constrainPan();
  }

  private _handlePointerUp(e: PointerEvent) {
    if (!this._isPanning) return;

    this._isPanning = false;

    if (this._diagramWrapper) {
      this._diagramWrapper.classList.remove('no-transition');
    }

    (e.target as HTMLElement).releasePointerCapture(e.pointerId);

    setTimeout(() => {
      this._isPanning = false;
    }, 10);
  }

  private _constrainPan() {
    const maxPan = Math.max(0, (this._zoom - 1) * 200);
    this._panX = Math.max(-maxPan, Math.min(maxPan, this._panX));
    this._panY = Math.max(-maxPan, Math.min(maxPan, this._panY));
  }

  private _zoomIn() {
    this._zoom = Math.min(this._zoom + 0.25, 5);
  }

  private _zoomOut() {
    const newZoom = Math.max(this._zoom - 0.25, 0.25);
    this._zoom = newZoom;

    if (newZoom <= 1) {
      this._panX = 0;
      this._panY = 0;
    } else {
      this._constrainPan();
    }
  }

  private _resetView() {
    this._zoom = 1;
    this._panX = 0;
    this._panY = 0;
  }

  private _fitToView() {
    this._zoom = 1;
    this._panX = 0;
    this._panY = 0;
  }

  removeEditor(portal: HTMLDivElement) {
    portal.remove();
  }

  override renderBlock() {
    const zoomInIcon = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35M11 8v6M8 11h6"/>
    </svg>`;

    const zoomOutIcon = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35M8 11h6"/>
    </svg>`;

    const fitIcon = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
    </svg>`;

    const transform = `scale(${this._zoom}) translate(${this._panX / this._zoom}px, ${this._panY / this._zoom}px)`;

    const content = this._error
      ? html`<div class="mermaid-error-placeholder">${this._error}</div>`
      : this._svgContent
        ? html`
            <div
              class="mermaid-viewport ${this.isZoomed ? 'zoomed' : ''} ${this._isPanning ? 'panning' : ''}"
              @wheel=${this._handleWheel}
              @pointerdown=${this._handlePointerDown}
              @pointermove=${this._handlePointerMove}
              @pointerup=${this._handlePointerUp}
              @pointercancel=${this._handlePointerUp}
            >
              <div
                class="mermaid-diagram-wrapper"
                style="transform: ${transform}"
                .innerHTML=${this._svgContent}
              ></div>
            </div>
            <div class="mermaid-pan-hint ${this.isZoomed ? 'visible' : ''}">
              Drag to pan, Ctrl+scroll to zoom
            </div>
          `
        : html`<div class="mermaid-empty-placeholder">Mermaid diagram</div>`;

    return html`
      <div
        contenteditable="false"
        class="mermaid-block-container"
        @click=${this._handleClick}
      >
        ${content}
        ${this._svgContent
          ? html`
              <div class="mermaid-controls">
                <button
                  class="mermaid-control-btn"
                  @click=${(e: Event) => {
                    e.stopPropagation();
                    this._zoomOut();
                  }}
                  title="Zoom out"
                >
                  ${zoomOutIcon}
                </button>
                <span
                  class="mermaid-zoom-label"
                  @click=${(e: Event) => {
                    e.stopPropagation();
                    this._resetView();
                  }}
                  title="Reset view"
                >
                  ${Math.round(this._zoom * 100)}%
                </span>
                <button
                  class="mermaid-control-btn"
                  @click=${(e: Event) => {
                    e.stopPropagation();
                    this._zoomIn();
                  }}
                  title="Zoom in"
                >
                  ${zoomInIcon}
                </button>
                ${this.isZoomed
                  ? html`
                      <button
                        class="mermaid-control-btn"
                        @click=${(e: Event) => {
                          e.stopPropagation();
                          this._fitToView();
                        }}
                        title="Fit to view"
                      >
                        ${fitIcon}
                      </button>
                    `
                  : ''}
              </div>
            `
          : ''}
      </div>
    `;
  }

  selectBlock() {
    this.host.selection.setGroup('note', [
      this.host.selection.create('block', {
        blockId: this.model.id,
      }),
    ]);
  }

  toggleEditor() {
    this._editorAbortController?.abort();
    this._editorAbortController = new AbortController();

    this.selection.setGroup('note', []);

    const portal = createLitPortal({
      template: html`<mermaid-editor-menu
        .model=${this.model}
        .doc=${this.doc}
        .host=${this.host}
        .abortController=${this._editorAbortController}
      ></mermaid-editor-menu>`,
      container: this,
      closeOnClickAway: true,
      abortController: this._editorAbortController,
      shadowDom: false,
      portalStyles: {
        position: 'absolute',
        inset: '0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 'var(--affine-z-index-popover)',
        background: 'rgba(0, 0, 0, 0.03)',
        backdropFilter: 'blur(1px)',
        borderRadius: '8px',
      },
    });

    this._editorAbortController.signal.addEventListener(
      'abort',
      () => {
        this.removeEditor(portal);
      },
      { once: true }
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-mermaid': MermaidBlockComponent;
  }
}
